<template>
  <div class="stats-page">
    <!-- Page Header -->
    <h2 class="title">
      {{ $t('userAdmin.statsHeader') }}
    </h2>

    <!-- Info Text -->
    <div class="infotext">
      {{ $t('userAdmin.statsInfo1') }}
      <span class="highlight">{{ statsData.currentVolunteers }}</span>
      {{ $t('userAdmin.statsInfo2') }}
      <span class="highlight">{{ statsData.currentActiveVolunteers }}</span>
      {{ $t('userAdmin.statsInfo5') }}
      <br />
      {{ $t('userAdmin.genderDistribution') }}
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.volunteerGenderDistribution.female) }}%</span
      >
      {{ $t('userAdmin.genderDistributionFemale') }},
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.volunteerGenderDistribution.male) }}%</span
      >{{ $t('userAdmin.genderDistributionMale') }},
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.volunteerGenderDistribution.other) }}%</span
      >
      {{ $t('userAdmin.genderDistributionOther') }}.

      <br />
      <br />
      {{ $t('userAdmin.statsInfo3') }}
      <span class="highlight">{{ statsData.currentCustomers }}</span>
      {{ $t('userAdmin.statsInfo4') }}
      <span class="highlight">{{ statsData.currentActiveCustomers }}</span>
      {{ $t('userAdmin.statsInfo5') }}
      <br />
      {{ $t('userAdmin.genderDistribution') }}
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.customerGenderDistribution.female) }}%</span
      >
      {{ $t('userAdmin.genderDistributionFemale') }},
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.customerGenderDistribution.male) }}%</span
      >
      {{ $t('userAdmin.genderDistributionMale') }},
      <span class="highlight"
        >{{ formatGenderPercentage(statsData.customerGenderDistribution.other) }}%</span
      >
      {{ $t('userAdmin.genderDistributionOther') }}.
    </div>

    <div class="stats-content">
      <div class="stats-buttons-row">
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsVolunteers')"
          click-event="volunteers"
          @volunteers="selectView('volunteers')"
          :primary="selectedView === 'volunteers'"
        />
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsActiveVolunteers')"
          click-event="activevolunteers"
          @activevolunteers="selectView('activeVolunteers')"
          :primary="selectedView === 'activeVolunteers'"
        />
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsCustomers')"
          click-event="customers"
          @customers="selectView('customers')"
          :primary="selectedView === 'customers'"
        />
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsActiveCustomers')"
          click-event="activecustomers"
          @activecustomers="selectView('activeCustomers')"
          :primary="selectedView === 'activeCustomers'"
        />
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsFriendPairs')"
          click-event="pairs"
          @pairs="selectView('pairs')"
          :primary="selectedView === 'pairs'"
        />
        <SprButton
          :size="5"
          :title="$t('userAdmin.statsGroupMembers')"
          click-event="groupMembers"
          @groupMembers="selectView('groupMembers')"
          :primary="selectedView === 'groupMembers'"
        />
      </div>

      <div class="statbox-row">
        <div class="info-box">
          <FriendStatBox
            :selectedData="getSelectedLabel(selectedView)"
            :startDate="formatDateForApi(startDate)"
            :endDate="formatDateForApi(endDate)"
            :startValue="statsData.startValue"
            :endValue="statsData.endValue"
            :cumulativeValue="statsData.cumulativeValue"
          />
        </div>
      </div>

      <div class="free-interval">
        <h3>{{ $t('userAdmin.selectFreeInterval') }}</h3>

        <div class="datepickers">
          <SPRDatepicker
            v-model="startDate"
            :initial="startDate"
            :title="$t('userAdmin.startDate')"
            :label="$t('userAdmin.startDate')"
            :placeholder="$t('userAdmin.selectDate')"
            :options="datepickerOptions"
            :limit="startDateLimit"
          />
          <SPRDatepicker
            v-model="endDate"
            :initial="endDate"
            :title="$t('userAdmin.endDate')"
            :label="$t('userAdmin.endDate')"
            :placeholder="$t('userAdmin.selectDate')"
            :options="datepickerOptions"
            :limit="endDateLimit"
          />
        </div>

        <div class="year-buttons">
          <SprButton
            :size="4"
            :title="$t('userAdmin.showPreviousYear')"
            click-event="previousYear"
            @previousYear="selectYear('previous')"
          />
          <SprButton
            :size="4"
            :title="$t('userAdmin.showCurrentYear')"
            click-event="currentYear"
            @currentYear="selectYear('current')"
          />
        </div>
      </div>

      <div v-show="loading" class="loading">
        <div class="spinner">
          <loading />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import loading from '../SPRLoading.vue'
  import cache from '../../mixins/cache.js'
  import SprButton from '../SPRButton.vue'
  import SPRDatepicker from '../SPRDatepicker.vue'
  import moment from 'moment'
  import FriendStatBox from './FriendStatBox.vue'

  import {
    FriendPoolStandardDto,
    FriendPoolNetDto,
    FriendManagementDto,
    GenderDistributionDto,
  } from '@/types/index'

  const GENDER_PERCENTAGE_DECIMALS = 0

  // the data object
  export interface TemplateComponentData {
    loading: boolean
    loaded: number
    pools: Array<FriendPoolStandardDto | FriendPoolNetDto> | undefined
    friendshipGroup: FriendManagementDto
    datepickerOptions: Record<string, any>
    startDate: string
    endDate: string
    statsData: {
      startValue: number
      endValue: number
      currentCustomers: number
      currentVolunteers: number
      currentActiveCustomers: number
      currentActiveVolunteers: number
      customerGenderDistribution: GenderDistributionDto
      volunteerGenderDistribution: GenderDistributionDto
      cumulativeValue: number
    }
    selectedView: string
  }

  export default Vue.extend({
    name: 'FriendManagementGroupStatistics',
    components: {
      loading,
      SprButton,
      FriendStatBox,
      SPRDatepicker,
    },
    mixins: [cache],
    props: {
      primary: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: '',
      },
      filter: {
        type: String,
        default: '',
      },
    },
    data(): TemplateComponentData {
      return {
        loading: false,
        pools: undefined,
        friendshipGroup: {
          id: 0,
          name: this.$t('userAdmin.toFriendshipLink').toString(),
          contactInfo: [],
          groupActivities: [],
          national: false,
          volunteerCount: 0,
          customerCount: 0,
          friendRequestFormAllowed: false,
          sections: [],
          sectionDtos: [],
          zipCodes: [],
          riskyFriendships: [],
          riskyFriendshipsCount: 0,
          netFriendship: false,
        },
        statsData: {
          startValue: 0,
          endValue: 0,
          currentCustomers: 0,
          currentVolunteers: 0,
          currentActiveCustomers: 0,
          currentActiveVolunteers: 0,
          customerGenderDistribution: {} as GenderDistributionDto,
          volunteerGenderDistribution: {} as GenderDistributionDto,
          cumulativeValue: 0,
        },
        selectedView: 'volunteers',
        loaded: 0,
        // ✅ Default to current year start and today in DD.MM.YYYY
        startDate: moment().startOf('year').format('DD.MM.YYYY'),
        endDate: moment().format('DD.MM.YYYY'),
        datepickerOptions: {
          inputStyle: {
            display: 'inline-block',
            padding: '10px 20px',
            'line-height': '22px',
            'font-size': '16px',
            border: '2px solid #DDDDDD',
            'box-shadow': 'none',
            'border-radius': '24px',
            color: '#5F5F5F',
            width: '100%',
          },
        },
      }
    },
    computed: {
      // End date cannot be earlier than startDate, and cannot be after today
      endDateLimit() {
        const from = this.startDate
          ? moment(this.startDate, ['DD.MM.YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : '1900-01-01'

        const to = moment()
          .add(1, 'day') // isBetween() used in BaseDatePicker.vue is exclusive, so this allows selecting today
          .format('YYYY-MM-DD')
        return [{ type: 'fromto', from, to }]
      },

      // Start date cannot be after endDate (if endDate exists). Otherwise allow very old dates.
      startDateLimit() {
        const from = '1900-01-01'
        const to = this.endDate
          ? moment(this.endDate, ['DD.MM.YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD') // optionally restrict start to <= today
        return [{ type: 'fromto', from, to }]
      },
    },
    watch: {
      startDate(newStart) {
        if (!this.endDate) return
        const s = moment(newStart, ['DD.MM.YYYY', 'YYYY-MM-DD'])
        const e = moment(this.endDate, ['DD.MM.YYYY', 'YYYY-MM-DD'])
        if (s.isAfter(e, 'day')) {
          // user moved start beyond current end — clear end so user must re-pick
          this.endDate = ''
        }
        this.updateStats()
      },
      endDate(newEnd) {
        if (!this.startDate || !newEnd) return
        const s = moment(this.startDate, ['DD.MM.YYYY', 'YYYY-MM-DD'])
        const e = moment(newEnd, ['DD.MM.YYYY', 'YYYY-MM-DD'])
        if (e.isBefore(s, 'day')) {
          // shouldn't happen if limits work, but guard anyway
          this.startDate = ''
        }
        this.updateStats()
      },
    },
    async mounted(): Promise<void> {
      //this.randomizeStats()

      await this.triggerFetch()
    },
    methods: {
      formatGenderPercentage(value: number | string | null | undefined): string {
        const parsedValue = Number(value)
        if (!Number.isFinite(parsedValue)) {
          return (0).toFixed(GENDER_PERCENTAGE_DECIMALS)
        }

        return parsedValue.toFixed(GENDER_PERCENTAGE_DECIMALS)
      },
      selectView(view: string): void {
        this.selectedView = view

        // Optionally refresh stats data per view
        this.fetchStatistics()
        //this.randomizeStats()
      },
      async fetchStatistics() {
        try {
          this.loading = true

          const payload = {
            startDate: this.formatDateForApi(this.startDate),
            endDate: this.formatDateForApi(this.endDate),
            friendManagementGroupId: this.id,
            dataType: this.selectedView, // "volunteers", "customers", etc.
          }

          let endpointKey = this.friendshipGroup.netFriendship
            ? 'getNetStatistics'
            : 'getStatistics'
          const url =
            this.$store.state.apiBases.friendservice +
            this.$store.state.apiEndpoints[endpointKey][0]

          const response = await this.$http.post(url, payload)

          if (response && response.data) {
            this.statsData.startValue = response.data.countStart
            this.statsData.endValue = response.data.countEnd
            this.statsData.currentVolunteers = response.data.currentVolunteerCount
            this.statsData.currentCustomers = response.data.currentCustomerCount
            this.statsData.currentActiveVolunteers = response.data.currentActiveVolunteerCount
            this.statsData.currentActiveCustomers = response.data.currentActiveCustomerCount
            this.statsData.customerGenderDistribution =
              response.data.customerGenderDistribution || {}
            this.statsData.volunteerGenderDistribution =
              response.data.volunteerGenderDistribution || {}
            this.statsData.cumulativeValue = response.data.countCumulative || 0
          }
        } catch (err) {
          console.error('Error fetching statistics:', err)
          this.displayError(err)
        } finally {
          this.loading = false
        }
      },
      getSelectedLabel(view: string): string {
        switch (view) {
          case 'volunteers':
            return this.$t('userAdmin.statsVolunteers') as string
          case 'customers':
            return this.$t('userAdmin.statsCustomers') as string
          case 'pairs':
            return this.$t('userAdmin.statsFriendPairs') as string
          case 'groupMembers':
            return this.$t('userAdmin.statsGroupMembers') as string
          case 'activeVolunteers':
            return this.$t('userAdmin.statsActiveVolunteers') as string
          case 'activeCustomers':
            return this.$t('userAdmin.statsActiveCustomers') as string
          default:
            return this.$t('userAdmin.statsVolunteers') as string
        }
      },
      updateStats() {
        if (!this.startDate || !this.endDate || !this.id) return

        this.fetchStatistics()
        const start = this.formatDateForApi(this.startDate)
        const end = this.formatDateForApi(this.endDate)

        // Replace this with your actual API call or computed logic
        // Generate some demo data
        //this.randomizeStats()
      },
      formatDateForApi(dateString: string): string {
        // Convert DD.MM.YYYY → YYYY-MM-DD
        return moment(dateString, ['DD.MM.YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
      },
      randomizeStats() {
        const base = Math.floor(Math.random() * 1000)
        const randomize = Math.random() < 0.5 ? -1 : 1

        // Limit diff so it can’t exceed base (prevents going below 0)
        const maxDiff = Math.min(200, base)
        const diff = Math.floor(Math.random() * maxDiff) * randomize

        this.statsData.startValue = base
        this.statsData.endValue = base + diff
      },
      selectYear(year: string): void {
        const now = moment()
        if (year === 'current') {
          this.startDate = moment().startOf('year').format('DD.MM.YYYY')
          this.endDate = now.format('DD.MM.YYYY')
        } else if (year === 'previous') {
          const prev = now.clone().subtract(1, 'year')
          this.startDate = prev.startOf('year').format('DD.MM.YYYY')
          this.endDate = prev.endOf('year').format('DD.MM.YYYY')
        }
      },
      triggerFetch: function () {
        this.loaded = 0
        // Guard against missing id
        if (!this.id) {
          console.warn('FriendManagementGroupStatistics: missing id prop')
          this.loaded = -1
          return
        }
        // Properly construct URL (previously left in expression, never assigned)
        const url =
          this.$store.state.apiBases.friendservice +
          this.$store.state.apiEndpoints.friendOrganizationById[0].replace('{0}', this.id)

        const self = this
        this.fetchItem(
          url,
          this.expSeconds(120),
          (s) => {
            self.friendshipGroup = s
            self.loaded = 1
            self.fetchStatistics()
          },
          () => {
            self.loaded = -1
          },
        )
      },
      displayError(err: Response | any): void {
        if (Array.isArray(err.body)) {
          err.body.forEach((x) => {
            // @ts-ignore
            this.showAlertError(x.errorMessage)
          })
        } else if (err.body && err.body.errorMessage) {
          // @ts-ignore
          this.showAlertError(err.errorMessage)
        } else if (err.bodyText) {
          // @ts-ignore
          this.showAlertError(err.bodyText)
        } else {
          // @ts-ignore
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      },
    },
  })
</script>
<style lang="scss" scoped>
  @import '~@/assets/scss/_variables.scss';

  .stats-page {
    background-color: $brand-grey1;
    color: $brand-font;
    padding: 0;
    font-family: 'Signa-Regular', sans-serif;

    .title {
      font-family: 'Signa-Bold', sans-serif;
      color: $brand-red;
      font-size: 1.5rem;
      margin-bottom: 1rem;
      line-height: 1.3;
    }

    .infotext {
      color: $brand-grey12;
      font-size: 1rem;
      margin-bottom: 2rem;
      line-height: 1.5;

      .highlight {
        font-weight: bold;
        color: $brand-black;
        margin: 0 0.25rem;
      }
    }

    .stats-content {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 1.5rem;
    }

    .stats-buttons-row {
      display: flex;
      flex-wrap: nowrap;
      align-items: stretch;
      overflow-x: auto;
      padding-bottom: 0.25rem;

      ::v-deep(button) {
        flex: 1 1 0;
        width: auto;
        justify-content: center;
        white-space: nowrap;
        font-size: 0.95rem;
      }
    }

    .statbox-row {
      width: 100%;
    }

    .free-interval {
      --interval-column-width: clamp(180px, 24vw, 260px);
      margin-top: 0.5rem;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;

      h3 {
        font-size: 1.1rem;
        font-family: 'Signa-Bold', sans-serif;
        color: $brand-red;
        margin-bottom: 0.25rem;
      }

      .datepickers {
        display: grid;
        grid-template-columns: repeat(2, var(--interval-column-width));
        justify-content: start;
        gap: 1rem;
        width: auto;

        ::v-deep(.datepicker-container) {
          width: 100%;
          min-width: 0;
        }
      }

      .year-buttons {
        display: grid;
        grid-template-columns: repeat(2, var(--interval-column-width));
        justify-content: start;
        gap: 1rem;
        width: auto;

        ::v-deep(button) {
          width: 100%;
          min-width: 0;
          justify-content: center;
        }
      }

      @media (max-width: 700px) {
        .datepickers,
        .year-buttons {
          grid-template-columns: 1fr;
        }
      }
    }

    .info-box {
      width: 100%;
      height: 375px;
      border: 2px solid $brand-red;
      border-radius: 16px;
      background-color: $brand-white;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      .info-text {
        color: $brand-grey13;
        font-family: 'Signa-Bold', sans-serif;
        font-size: 1.25rem;
      }
    }

    @media (max-width: 1100px) {
      .stats-buttons-row {
        ::v-deep(button) {
          min-width: 150px;
        }
      }
    }

    .loading {
      position: absolute;
      inset: 0;
      z-index: 20;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba($brand-white, 0.72);
      backdrop-filter: blur(1px);
    }

    .spinner {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
